import * as React from 'react'
import { useState, useEffect, useRef } from 'react'
import {
  Grid,
  Divider,
  Typography,
  useMediaQuery,
  useTheme,
  Container,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Rating,
} from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { showSnackbar } from '@context/SnackbarContext'
import WebService from '@util/webService'
import useAlert from '@hooks/useAlert'
import useIndicator from '@hooks/useIndicator'
import FlatButton from '@components/FlatButton'
import MyDialog2 from '@components/MyDialog2'
import MyPagination from '@components/MyPagination'
import MobileLabel from '@components/MobileLabel'
import ConfirmDialog from '@components/ConfirmDialog'
import axios from 'axios'

const boldFont = {
  fontWeight: 'bold',
}

const starColor = '#E53935'

const defPageInfo = { pageNum: 0, pageSize: 10 }

// 계약기간(prod_alias) 선택지: 저장값 그대로 공개 화면에 "계약기간: 6개월" 형태로 노출
const prodAliases = ['6개월', '12개월', '24개월']

const USER_ALIAS_MAX = 45
const CONTENT_MAX = 1000

// ReviewsRow 전 컬럼을 채워서 전송: Gson 역직렬화 시 null 필드가 생기지 않도록 함
const newReview = {
  id: -1,
  userAlias: '',
  prodAlias: prodAliases[0],
  star: 5,
  content: '',
  displayYn: 'Y',
}

// ReviewList 밖에 정의: 부모 재렌더 시 입력 중인 폼 상태가 초기화되지 않도록 함
const ReviewForm = ({ review, onSubmit }) => {
  const theme = useTheme()
  const [form, set_form] = useState({ ...newReview, ...review })
  const isNew = form.id === -1

  const setField = (field, value) => {
    set_form((prev) => ({ ...prev, [field]: value }))
  }

  const validate = () => {
    if (!form.userAlias?.trim()) {
      return '작성자를 입력해 주세요.'
    }
    if (!prodAliases.includes(form.prodAlias)) {
      return '계약기간을 선택해 주세요.'
    }
    if (!(form.star >= 1 && form.star <= 5)) {
      return '별점을 선택해 주세요.'
    }
    if (!form.content?.trim()) {
      return '내용을 입력해 주세요.'
    }
    return null
  }

  const onSave = () => {
    const msg = validate()
    if (msg) {
      showSnackbar(msg, theme.palette.warning.dark)
      return
    }
    onSubmit({
      ...form,
      userAlias: form.userAlias.trim(),
      content: form.content.trim(),
    })
  }

  return (
    <Grid container spacing={2} style={{ marginTop: 5 }}>
      {isNew ? null : (
        <Grid item xs={12}>
          <Typography variant='body2'>리뷰번호: {form.id}</Typography>
        </Grid>
      )}
      <Grid item xs={12} md={7}>
        <TextField
          fullWidth
          required
          label='작성자'
          placeholder='예: korea****'
          value={form.userAlias ?? ''}
          inputProps={{ maxLength: USER_ALIAS_MAX }}
          onChange={(e) => setField('userAlias', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={5}>
        <FormControl fullWidth required>
          <InputLabel id='prodAlias'>계약기간</InputLabel>
          <Select
            labelId='prodAlias'
            label='계약기간'
            value={form.prodAlias ?? ''}
            onChange={(e) => setField('prodAlias', e.target.value)}
          >
            {prodAliases.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} style={{ display: 'flex', alignItems: 'center' }}>
        <Typography style={{ marginRight: 10 }}>별점:</Typography>
        <Rating
          value={form.star}
          max={5}
          sx={{ color: starColor }}
          onChange={(e, v) => {
            // 같은 별을 다시 누르면 null 이 들어오므로 기존 값 유지 (최소 1점)
            if (v !== null) {
              setField('star', v)
            }
          }}
        />
        <Typography style={{ marginLeft: 8 }}>{form.star}</Typography>
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          required
          multiline
          minRows={3}
          label='내용'
          value={form.content ?? ''}
          inputProps={{ maxLength: CONTENT_MAX }}
          helperText={`${(form.content ?? '').length} / ${CONTENT_MAX}`}
          onChange={(e) => setField('content', e.target.value)}
        />
      </Grid>

      <Grid item xs={12} style={{ display: 'flex', alignItems: 'center' }}>
        <Typography style={{ marginRight: 10 }}>노출:</Typography>
        <RadioGroup row value={form.displayYn ?? 'N'} onChange={(e) => setField('displayYn', e.target.value)}>
          {['Y', 'N'].map((yn) => (
            <FormControlLabel key={yn} value={yn} label={yn} control={<Radio size='small' />} />
          ))}
        </RadioGroup>
      </Grid>

      <Grid item xs={12} style={{ display: 'flex', justifyContent: 'center' }}>
        <Button variant='contained' onClick={onSave}>
          저장
        </Button>
      </Grid>
    </Grid>
  )
}

const ReviewList = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const account = useSelector((state) => state.accountReducer)
  const navigate = useNavigate()
  const location = useLocation()
  const pack = location.state?.pack
  const curLocationRef = useRef(location)
  const { alert, renderAlert } = useAlert()
  const [openIndicator, closeIndicator, renderIndicator] = useIndicator()
  const [records, set_records] = useState([])
  const [totalCount, set_totalCount] = useState(0)
  const [pageInfo, set_pageInfo] = useState(pack?.data?.pageInfo ?? defPageInfo)
  // ref가 아닌 state로 관리: 다이얼로그 component prop이 선택된 리뷰로 갱신되도록 함
  const [editTarget, set_editTarget] = useState(newReview)
  const dgRef = useRef()

  const fetchReviews = async () => {
    const url = `/review/list`
    const resp = await WebService.get(url, {
      pageNum: pageInfo.pageNum,
      pageSize: pageInfo.pageSize,
    })

    if (resp.repCode !== 'ack') {
      alert(resp.repMessage)
      return
    }
    const repMessage = JSON.parse(resp.repMessage)
    const records = JSON.parse(repMessage.records)
    set_totalCount(+repMessage.totalCount)
    return records
  }

  const onLoad = async () => {
    const records = await fetchReviews()
    if (!records) {
      return
    }
    set_records(records)
  }

  const onOpenEdit = (review) => {
    set_editTarget({ ...review })
    dgRef.current.open()
  }

  const onSave = async (review) => {
    const url = `/review`
    const data = {
      review: review,
    }
    const formData = new FormData()
    const jsonData = JSON.stringify(data)
    formData.append('body', new Blob([jsonData], { type: 'application/json' }))

    openIndicator()
    const resp = await axios.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    closeIndicator()

    if (resp.data.repCode !== 'ack') {
      alert(resp.data.repMessage)
      return
    }
    dgRef.current.close()
    const isNew = review.id === -1
    showSnackbar(isNew ? '등록되었습니다.' : '수정되었습니다.')
    // 목록이 id desc 정렬이므로 신규 등록 시 첫 페이지로 이동
    set_pageInfo(isNew ? { ...defPageInfo } : { ...pageInfo })
  }

  const onDelete = async (reviewId) => {
    const url = `/review/${reviewId}`
    const resp = await WebService.delete(url)
    if (resp.repCode !== 'ack') {
      alert(resp.repMessage)
      return
    }
    showSnackbar('삭제되었습니다.')
    // 마지막 페이지의 마지막 한 건을 지운 경우 이전 페이지로 이동
    const lastPage = records.length === 1 && pageInfo.pageNum > 0
    set_pageInfo(lastPage ? { ...pageInfo, pageNum: pageInfo.pageNum - 1 } : { ...pageInfo })
  }

  useEffect(() => {
    if (curLocationRef.current.key !== location.key) {
      curLocationRef.current = location
      onLoad()
    }
  }, [location])

  useEffect(() => {
    if (!account?.user?.id) {
      navigate('/login')
    }
    else {
      onLoad()
    }
  }, [pageInfo])

  return (
    <div
      style={{
        paddingTop: isMobile ? 50 : 30,
        paddingBottom: 50,
        minHeight: '75vh',
      }}
    >
      {renderAlert()}
      {renderIndicator()}
      <MyDialog2
        title={editTarget.id === -1 ? '리뷰등록' : '리뷰수정'}
        ref={dgRef}
        component={<ReviewForm key={editTarget.id} review={editTarget} onSubmit={onSave} />}
        windowWidth='sm'
      />
      <Container>
        <Grid container spacing={2} style={{ alignItems: 'center' }}>
          <Grid
            item
            xs={12}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: 16 }}
          >
            <Typography style={boldFont} variant='h5'>
              리뷰목록
            </Typography>
            <Button variant='contained' onClick={() => onOpenEdit(newReview)}>
              리뷰등록
            </Button>
          </Grid>

          {isMobile ? null : (
            <Grid item xs={12}>
              <Grid container spacing={1} style={{ alignItems: 'center' }}>
                <Grid item md={1}>
                  <Typography style={boldFont}>번호</Typography>
                </Grid>
                <Grid item md={2}>
                  <Typography style={boldFont}>작성자</Typography>
                </Grid>
                <Grid item md={1}>
                  <Typography style={boldFont}>계약기간</Typography>
                </Grid>
                <Grid item md={2}>
                  <Typography style={boldFont}>별점</Typography>
                </Grid>
                <Grid item md={3}>
                  <Typography style={boldFont}>내용</Typography>
                </Grid>
                <Grid item md={1}>
                  <Typography style={boldFont}>노출</Typography>
                </Grid>
                <Grid item md={2}>
                  <Typography style={boldFont}>관리</Typography>
                </Grid>
              </Grid>
            </Grid>
          )}
          <Grid item xs={12}>
            <Divider />
          </Grid>

          {records.length > 0 ? (
            records.map((item) => (
              <Grid item xs={12} key={item.id}>
                <Grid container spacing={1} style={{ alignItems: 'center' }}>
                  <Grid item xs={6} md={1}>
                    <MobileLabel label='[번호]' />
                    <Typography variant='body2'>{item.id}</Typography>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <MobileLabel label='[작성자]' />
                    <Typography variant='body2' style={{ fontWeight: 'bold' }}>
                      {item.userAlias}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={1}>
                    <MobileLabel label='[계약기간]' />
                    <Typography variant='body2'>{item.prodAlias}</Typography>
                  </Grid>
                  <Grid item xs={6} md={2} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                    <MobileLabel label='[별점]' />
                    <Rating value={item.star} max={5} size='small' readOnly sx={{ color: starColor }} />
                    <Typography variant='body2' style={{ marginLeft: 4 }}>
                      {item.star}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <MobileLabel label='[내용]' />
                    <Typography variant='body2' style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                      {item.content}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={1}>
                    <MobileLabel label='[노출]' />
                    <Typography variant='body2'>{item.displayYn}</Typography>
                  </Grid>
                  <Grid item xs={12} md={2} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                    <FlatButton size='small' onClick={() => onOpenEdit(item)} style={{ marginRight: 5 }}>
                      수정
                    </FlatButton>
                    <ConfirmDialog
                      btn={<FlatButton size='small' color={'error'}>삭제</FlatButton>}
                      title={'주의'}
                      content={`[${item.userAlias}] 리뷰를 삭제하시겠습니까?`}
                      bc={'error'}
                      perform={() => {
                        onDelete(item.id)
                      }}
                    />
                  </Grid>
                </Grid>
                <Divider style={{ marginTop: 8 }} />
              </Grid>
            ))
          ) : (
            null
          )}

          <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <MyPagination totalCount={totalCount} pageInfo={pageInfo} set_pageInfo={set_pageInfo} />
          </Grid>
        </Grid>
      </Container>
    </div>
  )
}

export default ReviewList
